import React from 'react';
import { Box } from '@mui/material';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { translate as t } from 'hooks/useTranslations';

export type InheritanceType = 'same-as-parent' | 'modified-from-parent' | 'new-in-child';

interface InheritanceBadgeProps {
  type: InheritanceType;
  entity: 'field' | 'section';
  organizationId?: string | null;
}

/**
 * Shared badge component to display inheritance status (field or section) in a child template.
 * Shows whether the entity is same as parent, modified from parent, or new in child.
 * Labels differ based on template type:
 * - Global/jurisdiction templates (organizationId === null): parent/child terminology
 * - Agency templates (organizationId !== null): Lexipol/agency terminology
 */
export const InheritanceBadge: React.FC<InheritanceBadgeProps> = ({
  type,
  entity,
  organizationId = null,
}) => {
  const baseKey = `performanceReporting.templates.builder.${entity}Inheritance`;
  const translationPrefix =
    organizationId === null ? baseKey : `${baseKey}.agency`;

  const badgeConfig = {
    'same-as-parent': {
      color: 'info' as const,
      label: t(`${translationPrefix}.sameAsParent`),
    },
    'modified-from-parent': {
      color: 'primary' as const,
      label: t(`${translationPrefix}.modifiedFromParent`),
    },
    'new-in-child': {
      color: 'success' as const,
      label: t(`${translationPrefix}.newInChild`),
    },
  };

  const config = badgeConfig[type];

  return (
    <Box sx={{ '& .lex-badge': { borderRadius: '4px' } }}>
      <PhxBadge
        color={config.color}
        size='sm'
      >
        {config.label}
      </PhxBadge>
    </Box>
  );
};

export default InheritanceBadge;
